/**
 * Column definition factory for the orders table — `GET_ORDER_COLUMNS`.
 * Ported 1:1 from `_legacy/components/Orders/constants.js`.
 *
 * `isPatientPage = true` hides the three patient-identifying columns
 * (firstName / lastName / DOB) because the patient row already
 * supplies that context on the patient dashboard.
 *
 * Permission-gated columns require `permissions.ACCOUNTS_GET` (DS
 * scope) — the orders-table consumer drops them for non-DS users
 * via the standard `permission` column attribute the shared Table
 * primitive honours.
 */
import i18next from 'i18next';
import permissions from '@shared/config/permissions';
import { INFO_PANE_PAGES } from 'redux_store';
import FILTER_TYPES from '@shared/ui/Table/Filters/constants';
// eslint-disable-next-line no-restricted-imports
import { CUSTOM_TAGS_OPTIONS } from '_legacy/components/Shared/CustomTags';

export const GET_ORDER_COLUMNS = isPatientPage => [
  {
    size: 200,
    header: i18next.t('tables:customTags'),
    accessorKey: 'accountCustomTags',
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: CUSTOM_TAGS_OPTIONS,
    },
    cell: {
      withBubbles: true,
    },
    permission: permissions.ACCOUNTS_GET,
  },
  {
    size: 220,
    header: i18next.t('tables:account'),
    accessorKey: 'accountBusinessName',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    permission: permissions.ACCOUNTS_GET,
    enableHiding: false,
    isDefaultCell: true,
  },
  {
    size: 180,
    header: i18next.t('tables:customId'),
    accessorKey: 'accountCustomIdentifier',
    filter: {},
    permission: permissions.ACCOUNTS_GET,
    enableHiding: false,
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:mrnNumber'),
    accessorKey: 'patientMrnNumber',
    filter: {},
    hidden: isPatientPage,
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 240,
    header: i18next.t('tables:patientFirstName'),
    accessorKey: 'patientFirstName',
    filter: {},
    hidden: isPatientPage,
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 240,
    header: i18next.t('tables:patientLastName'),
    accessorKey: 'patientLastName',
    filter: {},
    hidden: isPatientPage,
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 200,
    header: i18next.t('tables:patientDOB'),
    accessorKey: 'patientDateOfBirth',
    hidden: isPatientPage,
    enableColumnFilterModes: false,
    isDefaultCell: true,
  },
  {
    size: 250,
    header: i18next.t('tables:orderType'),
    accessorKey: 'orderType',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:orderFormula'),
    accessorKey: 'orderFormula',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:orderDate'),
    accessorKey: 'orderDate',
    filter: {
      shouldRange: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:expectedDeliveryDate'),
    accessorKey: 'expectedDeliveryDate',
    filter: {
      shouldRange: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:status'),
    accessorKey: 'orderStatus',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
  },
];
